import React, { useState, useMemo } from 'react';
import { Button, Empty, Tooltip } from 'antd';
import Chevron from '../../../../assets/customSVG/ChevronCrm';
import Group from '../../../../assets/customSVG/Group';

const AudienceFilters = ({ 
  activeFilter, 
  setActiveFilter,
  audienceSets = [],
}) => {
  // State to control show more/less for audience list
  const [showAll, setShowAll] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  // Format the audience lists for display
  const formattedAudienceLists = useMemo(() => {
    return audienceSets.map(audience => ({
      ...audience,
      name: audience.name.charAt(0).toUpperCase() + audience.name.slice(1)
    }));
  }, [audienceSets]);

  // Calculate which items to display
  const displayLimit = 5;
  const shouldShowToggle = formattedAudienceLists.length > displayLimit;
  const displayCount = showAll ? formattedAudienceLists.length : displayLimit;
  const displayLists = formattedAudienceLists.slice(0, displayCount);

  const handleAudienceClick = (audience) => {
    // Set the active filter to this audience
    setActiveFilter({
      type: 'audience_set',
      id: audience.id,
      name: audience.name,
      description: audience.description,
      filters: audience?.details?.selection_criteria || {},
      added_contact_person_ids: audience?.details?.manually_added_contact_person_ids || [],
      removed_contact_person_ids: audience?.details?.manually_removed_contact_person_ids || []
    });
  };

  return (
    <div className="mb-2">
      <div 
        className="flex justify-between items-center px-4 py-3 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <p className="font14px-600 text-gray-800 mt-1">Audiences</p>
        <span className={`transition-transform duration-300 ${isExpanded ? 'transform rotate-180' : ''}`}>
          <Chevron />
        </span>
      </div>

      <div className={`transition-all duration-200 overflow-hidden ${isExpanded ? 'max-h-fit' : 'max-h-0'}`}>
        {displayLists.length > 0 ? (
          <>
            {displayLists.map((audience) => (
              <div 
                key={audience.id}
                className={`flex items-center justify-between mx-4 px-3 py-2 rounded-md cursor-pointer ${
                  activeFilter?.type === 'audience_set' && activeFilter?.id === audience.id 
                    ? 'bg-green-50' 
                    : 'hover:bg-slate-100'
                }`}
                onClick={() => handleAudienceClick(audience)}
              >
                <div className="flex items-center gap-3">
                  <div>
                    <Group height="20px" width="20px" fill={activeFilter?.type === 'audience_set' && activeFilter?.id === audience.id ? '#15803d' : '#6b7280'} />
                  </div>
                  <Tooltip title={audience.name} placement="topLeft">
                    <p className={`truncate max-w-[150px] ${activeFilter?.type === 'audience_set' && activeFilter?.id === audience.id ? 'text-green-700 font14px-500' : 'font14px-400 text-gray-700'}`}>
                      {audience.name.length > 12 ? `${audience.name.substring(0, 12)}...` : audience.name}
                    </p>
                  </Tooltip>
                </div>
              </div>
            ))}
            
            {shouldShowToggle && (
              <div className="text-center mt-2">
                <Button 
                  type="text" 
                  size="small"
                  className="text-xs text-green-600 p-0 font14px-400 flex items-center mx-auto"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAll(!showAll);
                  }}
                >
                  {showAll ? (
                    <>
                      Show less
                    </>
                  ) : (
                    <>
                      Show more
                    </>
                  )}
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-xs text-gray-500 px-4 py-4 text-center">
            <Empty 
              description="No audience sets found" 
              image={Empty.PRESENTED_IMAGE_SIMPLE} 
              className="text-xs"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AudienceFilters;
